import type { TNotificationCategoryPreferences } from "./notifications";
import type {
  EAutoJoinSettingPreference,
  EEmailRecapSettingPreference,
  ELanguagePreference,
  EThemePreference,
} from "./preference";
import type {
  ESubscriptionProductCode,
  ESubscriptionProductType,
  ESubscriptionStatus,
  ICost,
} from "./subscription";

export enum EProvider {
  GOOGLE = "Google",
  GITHUB = "GitHub",
  EMAIL = "Email",
  SSO = "SSO",
}

export interface IActiveSubscription {
  uuid: string;
  name: string;
  cost: ICost;
  productCode: ESubscriptionProductCode;
  typeCode: ESubscriptionProductType;
  status: ESubscriptionStatus;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancellationReason: string | null;
  createdAt: string;
  canceledAt: string | null;
  updatedAt: string;
}

export interface IUserProfile {
  uuid: string;
  // NOTE: NSAI-7736 — null for an account with no address: an SSO member an
  // organization identifies by NameID or attribute (and Telegram-only sign-ups).
  email: string | null;
  firstName: string;
  lastName: string;
  emailVerified: boolean;
  lastLogin: string;
  createdAt: string;
  isTourCompleted: boolean;
  isCaTourCompleted: boolean;
  provider: EProvider[];
  activeSubscription: IActiveSubscription | null;
  isSsoUser: boolean;
  // NOTE: NSAI-7736 — the IdP login name such a member is shown by instead.
  username?: string | null;
}

export interface IVerifyOtp {
  code: string;
}

export interface IUpdateUserPayload {
  firstName: string;
  lastName: string;
  // NOTE: NSAI-7736 — left out by an account that has no email to keep.
  email?: string;
  password?: string;
  newPassword?: string;
}

export interface IUpdateEmailPayload {
  email: string;
  verificationCode: string;
}

export interface IUpdatePasswordPayload {
  password: string;
  newPassword: string;
}

export interface IDeleteUserPayload {
  code: string;
}

export interface IUserPreferences {
  language?: ELanguagePreference;
  theme?: EThemePreference;
  emailRecapSetting?: EEmailRecapSettingPreference;
  autoJoinSetting?: EAutoJoinSettingPreference;
  notificationSetting?: TNotificationCategoryPreferences;
  // NOTE: Per-user memory on/off (NSAI-4111). Modeled as `boolean | null` to
  // match the wire shape: the backend returns `null` (the platform-default,
  // opt-out-not-set state), `true`, or `false`. Undefined = field absent.
  // Read from GET /user/preferences; the Memory tab toggle writes a boolean.
  userMemoryEnabled?: boolean | null;
  // NOTE: Voice name string — the valid list is provider-specific and comes
  // from the server via app-config (ttsVoices), not from a client enum.
  ttsVoice?: string | null;
}

export interface IUserSearchItem {
  uuid: string;
  // NOTE: "" (not null) for an account without an address — the server keeps
  // the old shape for clients that lowercase it.
  email: string;
  fullName: string | null;
  // NOTE: NSAI-7736 — the IdP login name of a member with no email; absent
  // from servers that predate it.
  username?: string | null;
}

export interface ISearchUsersParams {
  query: string;
  limit?: number;
  enabled?: boolean;
}

export interface ISearchUsersResponse {
  projectMembers: IUserSearchItem[];
  organizationMembers: IUserSearchItem[];
}

export interface IUseSearchUsersResult {
  data: ISearchUsersResponse;
  isLoading: boolean;
}

// NOTE: NSAI-3491 — the identity fields an API returns for the user who owns an
// entity (an automation, a probe). Deliberately the four a client needs to label
// a row: the server never serializes more of the user record than this.
export interface IEntityOwner {
  uuid: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  username?: string | null;
}

export interface IGetUserDisplayPartsProps {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  // NOTE: NSAI-7736 — the IdP login name, used where the email is missing.
  username?: string | null;
}

export interface IUserDisplayParts {
  fullName: string;
  email: string;
  // NOTE: NSAI-7736 — what identifies the person besides their name: the
  // email, else the IdP login name, else "". The second line of a person row.
  handle: string;
}

export interface IGetUserHandleProps {
  email?: string | null;
  username?: string | null;
}

export interface IFormatUserDisplayValueProps extends IGetUserDisplayPartsProps {
  defaultPlaceholder?: string | null;
}

export interface IFormatOwnerNameProps {
  owner?: IEntityOwner | null;
  placeholder?: string;
}
